import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, View, ScrollView, TextInput, TouchableOpacity, ActivityIndicator, Animated, Dimensions } from 'react-native';
import { Image } from 'expo-image';
import { Text } from '../../../components/ui/AppText';
import { extractYoutubeId, fetchYoutubeMetadata } from '../services/youtubeMetadata';
import { pickGalleryVideo } from '../services/communityVideosService';
import { useCreateVideo, useUploadVideo } from '../hooks/useVideoMutations';
import { VIDEO_SOURCE } from '../constants';
import { AREAS, getAreaDoCurso } from '../../../constants/cursos';
import { useAuth } from '../../../contexts/AuthContext';
import { styles } from './styles';

const SCREEN_HEIGHT = Dimensions.get('window').height;

const VideoComposerModal = ({ visible, onClose, section = 'updates', allowGallery = true }) => {
  const [source, setSource] = useState(VIDEO_SOURCE.YOUTUBE);
  // YouTube
  const [url, setUrl] = useState('');
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  // Galeria
  const [asset, setAsset] = useState(null);
  const [title, setTitle] = useState('');
  const [error, setError] = useState(null);

  // Área do conteúdo — é o que permite a Comunidade ordenar por afinidade.
  // Vem PRÉ-SELECIONADA com a área do curso de quem publica: quase sempre é a
  // certa, então etiquetar sai de graça em vez de virar mais um campo a
  // preencher. Se o perfil não tiver curso, nasce vazia e vira obrigatória.
  const { profile } = useAuth();
  const areaDoAutor = profile?.curso ? getAreaDoCurso(profile.curso) : null;
  const [area, setArea] = useState(areaDoAutor);
  useEffect(() => { setArea((atual) => atual ?? areaDoAutor); }, [areaDoAutor]);

  const createVideo = useCreateVideo();
  const uploadVideo = useUploadVideo();
  const pending = createVideo.isPending || uploadVideo.isPending;

  // ── Entrada/saída (mesmo padrão de CompetenciaDetailModal e LoginQuickView) ──
  // Com o `animationType="slide"` do RN, quem deslizava era o modal INTEIRO — e o
  // backdrop é filho dele, então o escurecimento subia junto, de baixo para cima.
  // Aqui a animação é nossa: o backdrop faz fade no lugar e só a folha desliza.
  const slideAnim = useRef(new Animated.Value(SCREEN_HEIGHT)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // O Modal precisa continuar montado durante a SAÍDA: com `visible` direto no
  // Modal ele sumiria no mesmo frame em que a prop vira false e a animação de
  // fechar nunca apareceria.
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      Animated.parallel([
        Animated.spring(slideAnim, { toValue: 0, tension: 50, friction: 10, useNativeDriver: true }),
        Animated.timing(fadeAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(slideAnim, { toValue: SCREEN_HEIGHT, duration: 250, useNativeDriver: true }),
        Animated.timing(fadeAnim, { toValue: 0, duration: 200, useNativeDriver: true }),
      ]).start(({ finished }) => {
        if (finished) setMounted(false);
      });
    }
  }, [visible, slideAnim, fadeAnim]);

  const reset = useCallback(() => {
    setSource(VIDEO_SOURCE.YOUTUBE);
    setUrl(''); setMeta(null); setLoadingMeta(false);
    setAsset(null); setTitle(''); setError(null); setArea(areaDoAutor);
  }, [areaDoAutor]);
  const handleClose = useCallback(() => { reset(); onClose?.(); }, [reset, onClose]);

  const handleUrlChange = useCallback(async (text) => {
    setUrl(text); setError(null); setMeta(null);
    const id = extractYoutubeId(text);
    // Antes isto era um `return` mudo: link não reconhecido não dava prévia nem
    // erro, e o botão continuava desabilitado — o usuário tocava em Publicar e
    // nada acontecia, sem nenhuma pista do motivo.
    if (!id) {
      if (text.trim().length > 0) {
        setError('Esse link não parece ser do YouTube. Copie o link direto do vídeo (Compartilhar › Copiar link).');
      }
      return;
    }
    setLoadingMeta(true);
    try {
      const m = await fetchYoutubeMetadata(id);
      setMeta({ youtubeId: id, ...m });
    } catch (e) {
      setError(e?.message || 'Não foi possível validar o vídeo');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const handlePickGallery = useCallback(async () => {
    setError(null);
    try {
      const picked = await pickGalleryVideo();
      if (picked) setAsset(picked);
    } catch (e) {
      setError(e?.message || 'Não foi possível selecionar o vídeo');
    }
  }, []);

  const handleSubmit = useCallback(() => {
    setError(null);
    if (source === VIDEO_SOURCE.YOUTUBE) {
      // Cada saída explica o motivo. O botão continua com a APARÊNCIA de
      // desabilitado quando falta algo, mas segue clicável de propósito: um
      // botão inerte não diz ao usuário o que fazer.
      if (loadingMeta) { setError('Aguarde: validando o vídeo...'); return; }
      if (!url.trim()) { setError('Cole o link do vídeo do YouTube.'); return; }
      if (!meta) {
        setError('Não foi possível validar esse link. Confira se o vídeo é público e permite incorporação.');
        return;
      }
      if (!area) { setError('Escolha a área do conteúdo.'); return; }
      createVideo.mutate(
        { youtubeId: meta.youtubeId, title: meta.title, thumbnailUrl: meta.thumbnailUrl, area, section },
        { onSuccess: handleClose, onError: (e) => setError(e?.message || 'Falha ao publicar') }
      );
    } else {
      if (!asset) { setError('Escolha um vídeo da galeria.'); return; }
      if (!title.trim()) { setError('Dê um título ao vídeo.'); return; }
      if (!area) { setError('Escolha a área do conteúdo.'); return; }
      uploadVideo.mutate(
        { asset, title: title.trim(), area, section },
        { onSuccess: handleClose, onError: (e) => setError(e?.message || 'Falha ao enviar o vídeo') }
      );
    }
  }, [source, section, meta, asset, title, url, area, loadingMeta, createVideo, uploadVideo, handleClose]);

  const canSubmit =
    !pending && !!area && (source === VIDEO_SOURCE.YOUTUBE ? !!meta : (!!asset && !!title.trim()));

  return (
    <Modal visible={mounted} transparent animationType="none" onRequestClose={handleClose}>
      <View style={styles.modalRoot}>
        <Animated.View style={[styles.modalBackdrop, { opacity: fadeAnim }]} pointerEvents="none" />
        <Animated.View style={[styles.modalSheet, { transform: [{ translateY: slideAnim }] }]}>
          <Text style={styles.modalTitle}>Compartilhar vídeo</Text>
          <ScrollView
            style={{ maxHeight: 460 }}
            contentContainerStyle={{ gap: 14 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >

          {allowGallery ? (
            <View style={styles.sourceTabs}>
              <TouchableOpacity
                style={[styles.sourceTab, source === VIDEO_SOURCE.YOUTUBE && styles.sourceTabActive]}
                onPress={() => setSource(VIDEO_SOURCE.YOUTUBE)}
              >
                <Text style={[styles.sourceTabText, source === VIDEO_SOURCE.YOUTUBE && styles.sourceTabTextActive]}>YouTube</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.sourceTab, source === VIDEO_SOURCE.UPLOAD && styles.sourceTabActive]}
                onPress={() => setSource(VIDEO_SOURCE.UPLOAD)}
              >
                <Text style={[styles.sourceTabText, source === VIDEO_SOURCE.UPLOAD && styles.sourceTabTextActive]}>Da galeria</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {source === VIDEO_SOURCE.YOUTUBE ? (
            <>
              <TextInput
                style={styles.input}
                placeholder="Cole o link do YouTube"
                placeholderTextColor="#6B6E7B"
                value={url}
                onChangeText={handleUrlChange}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {loadingMeta ? <ActivityIndicator /> : null}
              {meta ? (
                <View style={styles.preview}>
                  <Image source={{ uri: meta.thumbnailUrl }} style={styles.previewThumb} contentFit="cover" />
                  <Text style={styles.previewTitle} numberOfLines={3}>{meta.title}</Text>
                </View>
              ) : null}
            </>
          ) : (
            <>
              <TouchableOpacity style={styles.galleryBtn} onPress={handlePickGallery}>
                <Text style={styles.galleryBtnText}>
                  {asset ? '✓ Vídeo selecionado' : '＋ Escolher vídeo da galeria'}
                </Text>
              </TouchableOpacity>
              {asset ? (
                <Text style={styles.galleryPicked}>
                  Duração: {asset.duration ? Math.round(asset.duration / 1000) : '?'}s
                </Text>
              ) : (
                <Text style={styles.hint}>Vídeo de até 1 minuto.</Text>
              )}
              <TextInput
                style={styles.input}
                placeholder="Título do vídeo"
                placeholderTextColor="#6B6E7B"
                value={title}
                onChangeText={setTitle}
                maxLength={200}
              />
            </>
          )}

          {/* Etiqueta do conteúdo — é ela que alimenta a ordenação por afinidade
              da Comunidade. Fica depois do vídeo porque a decisão natural é
              "qual vídeo" e só então "sobre o quê". */}
          <View>
            <Text style={styles.fieldLabel}>Área do conteúdo</Text>
            <Text style={styles.fieldHint}>
              Ajuda a mostrar este vídeo para quem estuda a área.
            </Text>
            <View style={styles.areaRow}>
              {AREAS.map((item) => {
                const ativo = area === item.id;
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={[styles.areaChip, ativo && styles.areaChipActive]}
                    onPress={() => setArea(item.id)}
                  >
                    <Text style={[styles.areaChipText, ativo && styles.areaChipTextActive]}>
                      {item.icon} {item.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>

          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          </ScrollView>

          <View style={styles.modalActions}>
            <TouchableOpacity style={styles.btn} onPress={handleClose}>
              <Text style={styles.btnGhostText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.btn, styles.btnPrimary, !canSubmit && styles.btnDisabled]}
              onPress={handleSubmit}
              disabled={pending}
            >
              <Text style={styles.btnPrimaryText}>{pending ? 'Publicando...' : 'Publicar'}</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

export default VideoComposerModal;
